import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmployeeLayout } from '../../components/EmployeeLayout';
import { employeeAPI, notificationAPI } from '../../lib/api';
import { Calendar, Clock, Megaphone, Bell } from 'lucide-react';
import ClockStatusCard from '../../components/ClockStatusCard';
import OnboardingChecklistCard from '../../components/OnboardingChecklistCard';

function getWeekRange() {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  const mon = new Date(today);
  mon.setDate(today.getDate() + diff);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmt = (d) => d.toISOString().split('T')[0];
  return { start: fmt(mon), end: fmt(sun) };
}

function getNextTwoWeeksEnd() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}

function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

function formatDate(dateStr) {
  const opts = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return new Date().toLocaleDateString(undefined, opts);
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.restaurant_id) {
      setLoading(false);
      return;
    }
    const { start, end } = getWeekRange();
    const today = getTodayStr();
    const twoWeeksEnd = getNextTwoWeeksEnd();

    Promise.all([
      employeeAPI.getSchedule(user.restaurant_id, { start_date: today, end_date: twoWeeksEnd }),
      employeeAPI.getTimesheets({ start_date: start, end_date: end }),
      employeeAPI.getAnnouncements(user.restaurant_id),
      notificationAPI.getAll().catch(() => ({ data: [] })),
    ])
      .then(([schedulesRes, timesheetsRes, announcementsRes, notifsRes]) => {
        setUpcomingShifts((schedulesRes.data || []).slice(0, 5));
        const hours = (timesheetsRes.data || []).reduce((sum, t) => sum + (t.duration_hours || 0), 0);
        setWeeklyHours(hours);
        setAnnouncements((announcementsRes.data || []).slice(0, 3));
        const unread = Array.isArray(notifsRes?.data) ? notifsRes.data.filter(n => !n.read).length : 0;
        setUnreadNotifications(unread);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <EmployeeLayout>
      <div className="p-6 space-y-6" data-testid="employee-dashboard">
        {/* Welcome header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-700 rounded-xl p-6 text-white">
          <p className="text-slate-400 text-sm">Welcome back,</p>
          <h1 className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Manrope' }}>
            {user?.name || 'Employee'}
          </h1>
          <p className="text-slate-400 text-sm mt-1">{formatDate()}</p>
        </div>

        {/* Clock-in shortcut — reuses existing time-clock API */}
        <ClockStatusCard />

        {/* Onboarding checklist — auto-hides when complete */}
        <OnboardingChecklistCard />

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Hours This Week', value: weeklyHours.toFixed(1) + 'h', icon: Clock, color: 'text-orange-500', testId: 'stat-hours', to: '/employee/timesheets' },
            { label: 'Upcoming Shifts', value: upcomingShifts.length, icon: Calendar, color: 'text-blue-500', testId: 'stat-shifts', to: '/employee/schedule' },
            { label: 'Announcements', value: announcements.length, icon: Megaphone, color: 'text-purple-500', testId: 'stat-announcements', to: '/employee/announcements' },
            { label: 'Notifications', value: unreadNotifications, icon: Bell, color: 'text-pink-500', testId: 'stat-notifications', to: '/employee/notifications' },
          ].map(({ label, value, icon: Icon, color, testId, to }) => (
            <Link
              key={label}
              to={to}
              className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:border-orange-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">{label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1" data-testid={testId}>
                    {loading ? '—' : value}
                  </p>
                </div>
                <Icon className={`${color} opacity-80`} size={24} />
              </div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Shifts */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900">Upcoming Shifts</h2>
              <Link to="/employee/schedule" className="text-sm text-orange-500 hover:text-orange-600 font-medium" data-testid="view-schedule-link">
                View all
              </Link>
            </div>
            {loading ? (
              <div className="p-5 text-slate-400 text-sm">Loading...</div>
            ) : upcomingShifts.length === 0 ? (
              <div className="p-5 text-slate-400 text-sm">No upcoming shifts scheduled.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcomingShifts.map(shift => (
                  <li key={shift.id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{shift.date}</p>
                      <p className="text-xs text-slate-500">{shift.start_time} – {shift.end_time}</p>
                    </div>
                    {shift.role_name && (
                      <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-600 font-medium">
                        {shift.role_name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Recent Announcements */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900">Recent Announcements</h2>
              <Link to="/employee/announcements" className="text-sm text-orange-500 hover:text-orange-600 font-medium" data-testid="view-announcements-link">
                View all
              </Link>
            </div>
            {loading ? (
              <div className="p-5 text-slate-400 text-sm">Loading...</div>
            ) : announcements.length === 0 ? (
              <div className="p-5 text-slate-400 text-sm">No announcements yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {announcements.map(a => (
                  <li key={a.id} className="px-5 py-3">
                    <p className="text-sm font-medium text-slate-900">{a.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </EmployeeLayout>
  );
}
